<template>
  <v-card class="border">
    <v-card-title>{{ $ucfirst($t('consultationsDuringStudy')) }}</v-card-title>
    <div class="patient-overview-timeline-container">
      <div
          class="patient-overview-timeline"
          ref="timeline"
      >
        <div
            class="patient-overview-timeline__current"
            :style="{ width: currentTimeWidth }"
        ></div>
        <timeline-button
            v-for="(item, index) in encounters"
            :key="`${item.encounterDate}-${index}`"
            :color="getColor(item.status)"
            :status="item.status"
            :version="item.visit"
            :scheduled="item.status === 'scheduled'"
            :btnStyle="{left : percentageToButtonLeftPosition(getEncounterPercentage(item.visit))}"
        ></timeline-button>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import TimelineButton from '../../../components/Parts/TimelineButton.vue';

/**
 * Component PatientOverviewTimeline.vue
 *
 * @module PatientOverviewTimeline
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/APP_DASHBOARD/templates/views/Patient/Overview/PatientOverviewTimeline.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'PatientOverviewTimeline',
  components: {
    TimelineButton,
  },
  data() {
    return {
      visitMonthOffsets: [0, 1, 2, 6, 12, 18, 36],
      buttonWidth: 40,
      // Since the first 3 visits come really close to each other (within 1 month each). The timeline buttons are
      // compressed, this adds extra percentual space to visit 2 and 3, so there is more space between 1-2-3
      visitCorrections: {
        2 : 0.02, // 2%
        3 : 0.04 // 4%
      }
    };
  },
  computed: {
    ...mapGetters({
      visit1Date: 'patientSummary/visit1Date',
      SDMEncounters: 'patientSummary/SDMEncounters',
    }),
    totalVisits() {
      return this.visitMonthOffsets.length;
    },
    encounters() {
      const encounters = [];

      for (let visit = 1; visit <= 7; visit += 1) {
        const existingEncounter = this.SDMEncounters
          .find((item) => item.version === visit);

        if (existingEncounter) {
          const { status, version } = existingEncounter;
          encounters.push({
            ...existingEncounter,
            visit: version,
            class: `patient-overview-timeline__visit--${status}`,
          });
        } else {
          const encounterDate = this.getScheduledEncounterDate(visit);
          encounters.push({
            visit: visit,
            status: 'scheduled',
            class: `patient-overview-timeline__visit--scheduled`,
            encounterDate,
          });
        }
      }

      return encounters;
    },
    totalDays() {
      const date = moment(this.visit1Date).add(this.visitMonthOffsets[this.visitMonthOffsets.length - 1], 'M').toISOString();
      return this.countDaysFromStart(date);
    },
    currentTimeWidth() {
      const percentage = Math.max(this.getTimePercentage(), this.getEncounterPercentage(this.getLastCompletedVisit()))
      
      return `${Math.round(percentage * 100)}%`;
    },
  },
  methods: {
    getLastCompletedVisit() {
      return this.encounters.reduce((lastCompletedVisit, encounter) => {
        if (['done', 'skipped'].includes(encounter.status)) {
          return Math.max(lastCompletedVisit, encounter.visit);
        }
        return lastCompletedVisit;
      }, 1);
    },
    getScheduledEncounterDate(visit) {
      const months = this.visitMonthOffsets[visit - 1];
      return moment(this.visit1Date).add(months, 'M').toISOString();
    },
    getTimePercentage() {
      const days = this.countDaysFromStart(moment().toISOString());
      return this.daysToPercentage(days)
    },
    getEncounterPercentage(visit) {
      visit = Math.min(visit, this.totalVisits);

      const currentInterval = this.visitMonthOffsets[visit - 1];
      const maxIntervals = this.visitMonthOffsets[this.visitMonthOffsets.length - 1];

      let percentage = currentInterval/maxIntervals;

      if (this.visitCorrections[visit]) {
        percentage += this.visitCorrections[visit];
      }

      return percentage;
    },
    percentageToButtonLeftPosition(percentage) {
      return `calc(${Math.round(percentage * 100)}% - ${Math.round(this.buttonWidth/2)}px)`;
    },
    countDaysFromStart(date) {
      const start = moment(this.visit1Date);
      const end = moment(date);
      return end.diff(start, 'days');
    },
    daysToPercentage(days) {
      return (this.totalDays && days) ? days / this.totalDays : 0;
    },
    getColor(status) {
      switch(status) {
        case 'done': return '#4caf50';
        case 'skipped': return '#f44336';
        case 'inprogress': return '#37125a';
        default: return '';
      }
    }
  },
};
</script>

<style lang="scss">
.patient-overview-timeline-container{
  padding: 0 20px;
}

.patient-overview-timeline {
  background: #d6dbe4;
  width: 100%;
  height: 4px;
  margin: 20px 0;
  position: relative;
}

.patient-overview-timeline__current {
  position: absolute;
  top: 0;
  left: 0;
  height: 4px;
  background-color: #66bb6a;
}

.patient-overview-timeline__visit {
  width: 42px;
  height: 42px;
  top: -20px;
}

.patient-overview-timeline__visit--done {
  color: #fff;
  background-color: #66bb6a;
  border-color: #66bb6a;
}

.patient-overview-timeline__visit--skipped {
  color: #fff;
  background-color: #f44336;
  border-color: #f44336;
}
</style>
